import { Component, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BillingService } from '@galaxy/billing';
import { AppPrice, AppPricingService, FieldMask } from '@galaxy/marketplace-apps';
import { PartnerService } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Product } from '@vendasta/store';
import { BehaviorSubject, EMPTY, Observable, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { TRAINING_ROOT, SalesConfiguration, BreadCrumb } from '../constants';
import { TRAINING_RESOURCES_CONFIG_TOKEN, TrainingResourcesConfig } from '../tokens';
import { ProductPageService } from './product-page.service';
import { File } from '@vendasta/store';

@Component({
  templateUrl: './product-page.component.html',
  styleUrls: ['./product-page.component.scss'],
  standalone: false,
})
export class VaProductPageComponent {
  readonly productId$: Observable<string>;
  readonly product$: Observable<Product>;
  readonly breadCrumbs$: Observable<BreadCrumb[]>;
  readonly shouldShowPrice$: Observable<boolean>;
  readonly appPrice$: Observable<AppPrice>;
  readonly partnerCurrency$: Observable<string>;

  isLoaded$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(TRAINING_RESOURCES_CONFIG_TOKEN) private readonly trainingResourcesConfig: TrainingResourcesConfig,
    private readonly route: ActivatedRoute,
    private readonly productService: ProductPageService,
    private readonly appPricingService: AppPricingService,
    private readonly partnerService: PartnerService,
    private readonly alert: SnackbarService,
    private readonly translate: TranslateService,
    private readonly billingService: BillingService,
  ) {
    const partnerId$: Observable<string> = this.partnerService.getPartnerId();

    this.productId$ = this.route.params.pipe(map((params) => params.productId));
    this.product$ = combineLatest([
      this.productId$,
      this.trainingResourcesConfig.partnerId$,
      this.trainingResourcesConfig.marketId$,
    ]).pipe(
      switchMap(([productId, partnerId, marketId]) => {
        return this.productService.getProductById(productId, partnerId, marketId);
      }),
      map((product) => {
        if (+product.wholesalePrice === 0) {
          product.wholesalePrice = 0;
        }
        if (+product.wholesalePrice < 0) {
          product.wholesalePrice = -1;
        }
        return product;
      }),
    );
    this.product$ = combineLatest([this.product$, partnerId$]).pipe(
      map(([product, partnerId]) => {
        product.files = this.filterFiles(product.files, this.trainingResourcesConfig.canShowFilesForPartner, partnerId);
        return product;
      }),
    );

    this.appPrice$ = combineLatest([this.productId$, partnerId$]).pipe(
      switchMap(([productId, partnerId]) => {
        return this.appPricingService.getMultiPricing(
          [productId],
          new FieldMask({ paths: ['wholesale'] }),
          partnerId,
          null,
        );
      }),
      map((appPrices) => {
        return appPrices?.[0]?.pricesForContexts?.['wholesale'];
      }),
      catchError(() => {
        this.alert.openErrorSnack('TRAINING_RESOURCES.TRAINING_ISSUE');
        return EMPTY;
      }),
    );

    this.partnerCurrency$ = partnerId$.pipe(
      switchMap((partnerId) =>
        this.billingService.getMultiContract([partnerId]).pipe(map((resp) => resp[partnerId]?.currency ?? 'USD')),
      ),
      map((res) => {
        return res;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.shouldShowPrice$ = this.trainingResourcesConfig.salesConfiguration$.pipe(
      map((config: SalesConfiguration) => config.showProductWholesalePrice),
    );
    this.breadCrumbs$ = this.product$.pipe(
      map((product) => [
        { label: this.translate.instant('TRAINING_RESOURCES.RESOURCES_TITLE') },
        {
          label: this.translate.instant('TRAINING_RESOURCES.PRODUCTS_BREADCRUMB'),
          link: `/${TRAINING_ROOT}/products/`,
        },
        { label: product.name },
      ]),
    );
  }

  private filterFiles(
    files: File[],
    canShowFilesForPartner: ((partnerId: string, fileName: string) => boolean) | boolean,
    partnerId: string,
  ): File[] {
    return files.filter((file) => {
      if (typeof canShowFilesForPartner === 'function') {
        return file && file.name && canShowFilesForPartner(partnerId, file.name);
      } else if (typeof canShowFilesForPartner === 'boolean') {
        return file && file.name && canShowFilesForPartner;
      } else {
        return false;
      }
    });
  }
}
